import { createForYouBootstrapState } from "./features/forYou/bootstrapState";
import { createPlayLifecycle } from "./features/analytics/playLifecycle";
import { PlayerRuntime } from "./orchestration/PlayerRuntime";
import { AudioPlayer } from "./audio/AudioPlayer";
import { createPlayerStore } from "./core/store";
import { createPlayerBreadcrumbs } from "./core/createPlayerBreadcrumbs";
import { createPlayerActions } from "./core/actions";
import { createProgressState } from "./core/progressState";
import { createResumeCheckpoint } from "./features/resume/resumeCheckpoint";
import { createSongsApi } from "./data/songs/api";
import { createCollectionsApi } from "./data/collections/api";
import { createArtistsApi } from "./data/artists/api";
import { createCollectionCache } from "./data/collections/cache";
import { createLocalSongAssets } from "./data/localAudioPaths";
import { createForYouApi } from "./features/forYou/api";
import { prepareTrack } from "./audio/prepareTrack";
import { ensureSongPlayable } from "./features/onDemandPublish/ensureSongPlayable";
import type { DataSession } from "../../data/core/session";
import type { DevicePorts } from "../../runtime/ports";
import type { PlayerSliceDeps } from "./core/deps";

export interface PlayerDeps
  extends Pick<
    PlayerSliceDeps,
    | "addAudioBreadcrumb"
    | "observeOfflineAccess"
    | "waitForOfflineAccessUser"
    | "showOfflineAccessPrompt"
    | "toast"
    | "afterInteractions"
    | "now"
    | "isOnline"
  > {
  audio: DevicePorts["audio"];
  kv: DevicePorts["kv"];
  resumeKv: DevicePorts["kv"];
  data(): DataSession;
}

export interface Player {
  audio: AudioPlayer;
  controller: PlayerRuntime;
  lifecycle: ReturnType<typeof createPlayLifecycle>;
  store: ReturnType<typeof createPlayerStore>["store"];
  actions: ReturnType<typeof createPlayerActions>;
  progress: ReturnType<typeof createProgressState>;
  resume: ReturnType<typeof createResumeCheckpoint>;
  catalog: PlayerSliceDeps["catalog"];
  getPlayerStoreHydrationDiagnostics(): Record<string, unknown>;
  getPlayerStoreHydrationFlatDiagnostics: ReturnType<
    typeof createPlayerStore
  >["getPlayerStoreHydrationFlatDiagnostics"];
  bootstrap: ReturnType<typeof createForYouBootstrapState>;
  breadcrumbs: ReturnType<typeof createPlayerBreadcrumbs>;
  reset(): void;
  dispose(): void;
}

export function createPlayer(deps: PlayerDeps): Player {
  const lifecycle = createPlayLifecycle();
  const controller = new PlayerRuntime();
  const audio = new AudioPlayer(deps.audio, lifecycle);
  const progress = createProgressState();
  const resume = createResumeCheckpoint(deps.resumeKv, deps.now);
  // A data generation can end while background audio keeps its player.
  const catalog = {
    fetchSongsByIds: (
      ...args: Parameters<ReturnType<typeof createSongsApi>["fetchSongsByIds"]>
    ) => createSongsApi(deps.data()).fetchSongsByIds(...args),
    fetchSongById: (
      ...args: Parameters<ReturnType<typeof createSongsApi>["fetchSongById"]>
    ) => createSongsApi(deps.data()).fetchSongById(...args),
    fetchFreshSongById: (
      ...args: Parameters<
        ReturnType<typeof createSongsApi>["fetchFreshSongById"]
      >
    ) => createSongsApi(deps.data()).fetchFreshSongById(...args),
    fetchSongsForCollection: (
      ...args: Parameters<
        ReturnType<typeof createCollectionsApi>["fetchSongsForCollection"]
      >
    ) => createCollectionsApi(deps.data()).fetchSongsForCollection(...args),
    fetchSimilarSongsForSongIds: (
      ...args: Parameters<
        ReturnType<typeof createCollectionsApi>["fetchSimilarSongsForSongIds"]
      >
    ) => createCollectionsApi(deps.data()).fetchSimilarSongsForSongIds(...args),
    fetchArtistPopularSongs: (
      ...args: Parameters<
        ReturnType<typeof createArtistsApi>["fetchArtistPopularSongs"]
      >
    ) => createArtistsApi(deps.data()).fetchArtistPopularSongs(...args),
    tryGetSongsFromCache: (
      ...args: Parameters<
        ReturnType<typeof createCollectionCache>["tryGetSongsFromCache"]
      >
    ) => createCollectionCache(deps.data()).tryGetSongsFromCache(...args),
    fetchForYouRecommendedSongs: (
      ...args: Parameters<
        ReturnType<typeof createForYouApi>["fetchForYouRecommendedSongs"]
      >
    ) => createForYouApi(deps.data()).fetchForYouRecommendedSongs(...args),
    ...createLocalSongAssets(deps.data().db),
  };
  const { store, ...hydration } = createPlayerStore(
    {
      ...deps,
      playLifecycle: lifecycle,
      audio,
      progress,
      resume,
      catalog,
      prepareTrack: (song, options) =>
        prepareTrack(song, options, catalog, deps.data()),
      ensureSongPlayable: (song) =>
        ensureSongPlayable(song, catalog.fetchFreshSongById, deps.data()),
    },
    deps.kv,
  );
  const breadcrumbs = createPlayerBreadcrumbs(
    audio,
    progress.progressState,
    deps.addAudioBreadcrumb,
  );
  const actions = createPlayerActions(store, breadcrumbs.addPlayerBreadcrumb);
  return {
    audio,
    controller,
    lifecycle,
    store,
    actions,
    progress,
    resume,
    catalog,
    breadcrumbs,
    bootstrap: createForYouBootstrapState(),
    ...hydration,
    reset: () => store.getState().actions.reset(),
    dispose() {
      controller.dispose();
      audio.dispose();
    },
  };
}
